export interface PickedFile {
  file: File;
  /** Directory portion, POSIX separators, '' for a top-level file. */
  relativePath: string;
}

/**
 * Pull files out of a drop, following directories. `webkitGetAsEntry` is the
 * only way to see inside a dropped folder and it works in Safari, Chrome and
 * Firefox; browsers without it still give us the flat file list.
 */
export async function filesFromDataTransfer(dt: DataTransfer): Promise<PickedFile[]> {
  const items = Array.from(dt.items ?? []);
  const entries: FileSystemEntry[] = [];

  for (const item of items) {
    if (item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }

  if (entries.length === 0) {
    return Array.from(dt.files ?? []).map((file) => ({ file, relativePath: '' }));
  }

  const out: PickedFile[] = [];
  for (const entry of entries) {
    await walkEntry(entry, '', out);
  }
  return out;
}

async function walkEntry(entry: FileSystemEntry, prefix: string, out: PickedFile[]): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File | null>((resolve) => {
      (entry as FileSystemFileEntry).file(resolve, () => resolve(null));
    });
    if (file) out.push({ file, relativePath: prefix });
    return;
  }

  if (entry.isDirectory) {
    const dir = entry as FileSystemDirectoryEntry;
    const reader = dir.createReader();
    const nested = prefix ? `${prefix}/${entry.name}` : entry.name;

    // readEntries returns at most 100 entries per call; keep asking until empty
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((resolve) => {
        reader.readEntries(resolve, () => resolve([]));
      });
      if (batch.length === 0) break;
      for (const child of batch) await walkEntry(child, nested, out);
    }
  }
}

/** Files from an <input>, keeping the folder structure when webkitdirectory was used. */
export function filesFromInput(list: FileList | null): PickedFile[] {
  if (!list) return [];
  return Array.from(list).map((file) => {
    const full = (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? '';
    const dir = full.includes('/') ? full.slice(0, full.lastIndexOf('/')) : '';
    return { file, relativePath: dir };
  });
}
